/**
 * Mixin for AG-Grid custom filters to prevent unwanted dialog closure
 * when interacting with operator selects and other filter elements.
 */
export abstract class AgFilterEventPreventionMixin {
  protected operatorSelectDocumentClickListener: ((event: MouseEvent) => void) | null = null;
  protected operatorSelectKeydownListener: ((event: KeyboardEvent) => void) | null = null;
  protected documentClickListener: ((event: MouseEvent) => void) | null = null;
  protected documentKeyboardListener: ((event: KeyboardEvent) => void) | null = null;

  /**
   * Adds document listeners to prevent operator select from closing the filter dialog
   */
  protected addOperatorSelectDocumentClickListener(): void {
    this.removeOperatorSelectDocumentClickListener();

    this.operatorSelectDocumentClickListener = (event: MouseEvent): void => {
      const target: HTMLElement = event.target as HTMLElement;
      const isInOperatorPanel: boolean = target.closest('.ag-filter-operator-panel') !== null;
      const isInOperatorSelect: boolean = target.closest('.ag-filter-operator-select') !== null;

      if (isInOperatorPanel || isInOperatorSelect) {
        event.stopPropagation();
      }
    };

    this.operatorSelectKeydownListener = (event: KeyboardEvent): void => {
      const target: HTMLElement = event.target as HTMLElement;
      const isInOperatorPanel: boolean = target.closest('.ag-filter-operator-panel') !== null;
      const isInOperatorSelect: boolean = target.closest('.ag-filter-operator-select') !== null;

      if (isInOperatorPanel || isInOperatorSelect) {
        event.stopPropagation();
      }
    };

    document.addEventListener('mousedown', this.operatorSelectDocumentClickListener, {capture: true});
    document.addEventListener('keydown', this.operatorSelectKeydownListener, {capture: false});
    document.addEventListener('keyup', this.operatorSelectKeydownListener, {capture: false});
  }

  /**
   * Removes operator select document listeners
   */
  protected removeOperatorSelectDocumentClickListener(): void {
    if (this.operatorSelectDocumentClickListener) {
      document.removeEventListener('mousedown', this.operatorSelectDocumentClickListener, {capture: true});
      this.operatorSelectDocumentClickListener = null;
    }
    if (this.operatorSelectKeydownListener) {
      document.removeEventListener('keydown', this.operatorSelectKeydownListener, {capture: false});
      document.removeEventListener('keyup', this.operatorSelectKeydownListener, {capture: false});
      this.operatorSelectKeydownListener = null;
    }
  }

  /**
   * Removes all document listeners (both operator select and general document listeners)
   */
  protected removeDocumentClickListener(): void {
    if (this.documentClickListener) {
      document.removeEventListener('mousedown', this.documentClickListener, {capture: true});
      this.documentClickListener = null;
    }
    if (this.documentKeyboardListener) {
      document.removeEventListener('keydown', this.documentKeyboardListener, {capture: false});
      document.removeEventListener('keyup', this.documentKeyboardListener, {capture: false});
      this.documentKeyboardListener = null;
    }
  }

  /**
   * Handler for operator select opened/closed state changes
   * Call this from your component's onOperatorSelectOpenedChange method
   */
  protected onOperatorSelectOpenedChange(opened: boolean): void {
    if (opened) {
      this.addOperatorSelectDocumentClickListener();
    } else {
      this.removeOperatorSelectDocumentClickListener();
    }
  }
}
